import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Checkbox,
  InputText,
  SectionCard,
  Select,
  type SelectOption,
  PageWrapper,
} from "@/deps/ui-library";
import { api } from "@/api";
import { HttpError } from "@/api/http";
import { useAccounts, useJournal, usePostJournal, useSaveJournal } from "@/api/queries";
import type { Account, Id, Party } from "@/api/types";
import { useCurrentSession } from "@/app/session";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { journalAccounts, journalRefusal } from "@/lib/accountPickers";
import { formatAmount } from "@/lib/money";
import { DateField } from "@/components/DateField";

/** The API's refusals of an account in a journal (ADR-0066), answered 422. */
const ACCOUNT_REFUSALS = [
  "account_not_journal_postable",
  "account_needs_adjusting_journal",
] as const;

interface Row {
  key: number;
  accountId: Id;
  party: Party | null;
  memo: string;
  debit: string;
  credit: string;
}

let nextKey = 1;
const emptyRow = (): Row => ({
  key: nextKey++,
  accountId: "",
  party: null,
  memo: "",
  debit: "",
  credit: "",
});

const toPaisa = (value: string) => Math.round((Number(value) || 0) * 100);

/** Customers, suppliers and employees: a journal can move any of their balances. */
async function searchParties(q: string) {
  const [customers, suppliers, employees] = await Promise.all([
    api.parties.search("customer", q),
    api.parties.search("supplier", q),
    api.parties.search("employee", q),
  ]);
  return [...customers, ...suppliers, ...employees];
}

/** Both the new voucher and an open draft. Posting saves first, so what posts is what is shown. */
export function JournalEditorPage() {
  const { id: routeId } = useParams({ strict: false }) as { id?: Id };
  // A refused posting leaves a saved draft. The next attempt revises it rather than adding a copy,
  // and the person stays here, with the reason on screen.
  const [savedId, setSavedId] = useState<Id | undefined>(undefined);
  const id = routeId ?? savedId;
  const navigate = useNavigate();
  const { session } = useCurrentSession();
  const { data: accounts = [] } = useAccounts();
  const { data: existing } = useJournal(routeId ?? "", routeId !== undefined);
  const save = useSaveJournal();
  const post = usePostJournal();

  // The year-end card opens this with the date a closing journal has to have, and what it's for.
  const start = useSearch({ strict: false }) as {
    date?: string;
    narration?: string;
    adjusting?: boolean;
  };
  const [date, setDate] = useState(() => start.date ?? new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState(start.narration ?? "");
  const [adjusting, setAdjusting] = useState(start.adjusting === true);
  const [reverses, setReverses] = useState(false);
  const [rows, setRows] = useState<Row[]>(() => [emptyRow(), emptyRow()]);

  useEffect(() => {
    if (!existing) return;
    setDate(existing.date);
    setNarration(existing.narration);
    setAdjusting(existing.isAdjusting);
    setReverses(existing.reversesNextYear);
    setRows(
      existing.lines.map((line) => ({
        key: nextKey++,
        accountId: line.accountId,
        party: line.partyId
          ? { id: line.partyId, kind: "customer", name: line.partyName ?? line.partyId }
          : null,
        memo: line.memo ?? "",
        debit: Number(line.debit) === 0 ? "" : line.debit,
        credit: Number(line.credit) === 0 ? "" : line.credit,
      })),
    );
  }, [existing]);

  // Control accounts, the calculated 3200, inactive accounts, and VAT unless the entry is adjusting (and the
  // company VAT registered) aren't offered (ADR-0066). A saved draft's own account stays on its line, so it
  // shows; the API refuses it when it shouldn't be there, and the refusal is shown on that line.
  const offered = journalAccounts(accounts, {
    vatRegistered: session.organization.vatRegistered,
    adjusting,
  });
  const option = (account: Account): SelectOption => ({
    value: account.id,
    label: `${account.code} · ${account.name}`,
  });
  const optionsFor = (row: Row): SelectOption[] => {
    const own = accounts.find((account) => account.id === row.accountId);
    return own && !offered.includes(own)
      ? [...offered.map(option), option(own)]
      : offered.map(option);
  };
  const debits = rows.reduce((sum, row) => sum + toPaisa(row.debit), 0);
  const credits = rows.reduce((sum, row) => sum + toPaisa(row.credit), 0);
  const filled = rows.filter(
    (row) => row.accountId !== "" && toPaisa(row.debit) + toPaisa(row.credit) > 0,
  );

  const update = (key: number, patch: Partial<Row>) =>
    setRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const postProblem =
    debits !== credits
      ? "Debits must equal credits."
      : debits === 0
        ? "Enter at least one balanced pair of lines."
        : narration.trim() === ""
          ? "Say why this entry exists."
          : null;
  const busy = save.isPending || post.isPending;

  const draft = () => ({
    id,
    date,
    currency: session.organization.currency,
    narration,
    isAdjusting: adjusting,
    reversesNextYear: adjusting && reverses,
    lines: filled.map((row) => ({
      accountId: row.accountId,
      partyId: row.party?.id ?? null,
      memo: row.memo,
      debit: (toPaisa(row.debit) / 100).toFixed(2),
      credit: (toPaisa(row.credit) / 100).toFixed(2),
    })),
  });

  const failure = save.error ?? post.error;

  // The chart's 422 (ADR-0066) is about an account, so it goes on the lines that name one it refuses — for as
  // long as they still do. Any other refusal is about the journal, and goes above it.
  const accountRefusal =
    failure instanceof HttpError
      ? ACCOUNT_REFUSALS.find((code) => failure.problem.type.endsWith(`/${code}`))
      : undefined;
  const lineRefusal = (row: Row): string | undefined => {
    const account = accounts.find((a) => a.id === row.accountId);
    return accountRefusal && account && journalRefusal(account, { adjusting }) === accountRefusal
      ? (failure as Error).message
      : undefined;
  };
  const onALine = accountRefusal !== undefined && rows.some((row) => lineRefusal(row));

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title={routeId ? "Edit journal voucher" : "New journal voucher"}
        actions={
          <>
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => void navigate({ to: "/accounting/journals" })}
            >
              Cancel
            </Button>
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              disabled={busy || narration.trim() === ""}
              onClick={() =>
                save.mutate(draft(), {
                  onSuccess: (saved) =>
                    void navigate({ to: "/accounting/journals/$id", params: { id: saved } }),
                })
              }
            >
              Save draft
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              title={postProblem ?? undefined}
              disabled={busy || postProblem !== null}
              onClick={() =>
                save.mutate(draft(), {
                  onSuccess: (saved) => {
                    setSavedId(saved);
                    post.mutate(saved, {
                      onSuccess: () =>
                        void navigate({ to: "/accounting/journals/$id", params: { id: saved } }),
                    });
                  },
                })
              }
            >
              {post.isPending ? "Posting" : "Post"}
            </Button>
          </>
        }
      />

      {failure && !onALine ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(failure as Error).message}
        />
      ) : null}

      <SectionCard title="Journal">
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 md:grid-cols-[200px_1fr]">
            <DateField label="Date" required value={date} onChange={setDate} />
            <InputText
              label="Narration"
              required
              placeholder="Why this entry exists"
              value={narration}
              onChange={(event) => setNarration(event.target.value)}
            />
          </div>
          <Checkbox
            checked={adjusting}
            onCheckedChange={(details) => setAdjusting(details.checked === true)}
            label="Adjusting entry"
          />
          {adjusting ? (
            <Checkbox
              checked={reverses}
              onCheckedChange={(details) => setReverses(details.checked === true)}
              label="Reverse on the first day of next year"
            />
          ) : null}
        </div>
      </SectionCard>

      <SectionCard title="Lines">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">Journal lines</caption>
            <thead>
              <tr className="border-border border-b text-xs font-medium">
                <th
                  scope="col"
                  className="text-foreground min-w-56 px-2 py-1.5 text-left font-medium"
                >
                  Account
                </th>
                <th
                  scope="col"
                  className="text-foreground min-w-52 px-2 py-1.5 text-left font-medium"
                >
                  Party
                </th>
                <th
                  scope="col"
                  className="text-foreground min-w-52 px-2 py-1.5 text-left font-medium"
                >
                  Description
                </th>
                <th scope="col" className="text-foreground w-32 px-2 py-1.5 text-right font-medium">
                  Debit
                </th>
                <th scope="col" className="text-foreground w-32 px-2 py-1.5 text-right font-medium">
                  Credit
                </th>
                <th scope="col" className="w-8">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.key} className="border-border border-b">
                  <td className="px-2 py-1 align-top">
                    <Select
                      aria-label={`Account, line ${index + 1}`}
                      placeholder="Choose an account"
                      options={optionsFor(row)}
                      value={row.accountId}
                      errorText={lineRefusal(row)}
                      showClearButton={false}
                      onChange={(details) => {
                        const picked = Array.isArray(details.value)
                          ? details.value[0]
                          : details.value;
                        if (picked) update(row.key, { accountId: String(picked.value) });
                      }}
                    />
                  </td>
                  <td className="px-2 py-1 align-top">
                    <PartyCombobox
                      label={`Party, line ${index + 1}`}
                      hideLabel
                      value={row.party}
                      onChange={(party) => update(row.key, { party })}
                      search={searchParties}
                    />
                  </td>
                  <td className="px-2 py-1 align-top">
                    <InputText
                      aria-label={`Description, line ${index + 1}`}
                      value={row.memo}
                      onChange={(event) => update(row.key, { memo: event.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1 align-top">
                    <InputText
                      aria-label={`Debit, line ${index + 1}`}
                      inputMode="decimal"
                      className="num text-right"
                      value={row.debit}
                      onChange={(event) =>
                        update(row.key, { debit: event.target.value, credit: "" })
                      }
                    />
                  </td>
                  <td className="px-2 py-1 align-top">
                    <InputText
                      aria-label={`Credit, line ${index + 1}`}
                      inputMode="decimal"
                      className="num text-right"
                      value={row.credit}
                      onChange={(event) =>
                        update(row.key, { credit: event.target.value, debit: "" })
                      }
                    />
                  </td>
                  <td className="px-1 py-1 align-top">
                    <Button.IconButton
                      icon="Trash2"
                      aria-label={`Remove line ${index + 1}`}
                      colorScheme="default"
                      variant="ghost"
                      size="sm"
                      disabled={rows.length <= 2}
                      onClick={() => setRows(rows.filter((other) => other.key !== row.key))}
                    />
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="px-2 py-2">
                  <Button
                    colorScheme="default"
                    variant="outline"
                    size="sm"
                    onClick={() => setRows([...rows, emptyRow()])}
                  >
                    Add row
                  </Button>
                </td>
                <td className="num text-foreground px-2 py-2 text-right font-medium">
                  {formatAmount((debits / 100).toFixed(2))}
                </td>
                <td className="num text-foreground px-2 py-2 text-right font-medium">
                  {formatAmount((credits / 100).toFixed(2))}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        {debits !== credits ? (
          <p role="status" className="text-warn-text mt-2 text-sm">
            Debits and credits differ by{" "}
            {formatAmount((Math.abs(debits - credits) / 100).toFixed(2))}.
          </p>
        ) : null}
      </SectionCard>
    </PageWrapper>
  );
}
